import { useEffect, useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { ChevronDown, LogOut, LayoutDashboard, Inbox, Users, Database, Trophy, Video,
  Flag,
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useAuthStore } from '@/stores/auth';
import { BrandLogo } from '@/components/brand/BrandLogo';
import { MobileNav } from './MobileNav';
import { api } from '@/lib/api';

function useInboxCount(): number {
  const [count, setCount] = useState(0);
  const { user } = useAuthStore();

  useEffect(() => {
    if (!user?.role || user.role !== 'admin') return;

    let cancelled = false;

    api
      .get<{ count: number }>('/tracks/inbox-count', { silent: true })
      .then((data) => {
        if (!cancelled) setCount(data.count);
      })
      .catch(() => {
        // Non-critical — badge just won't show
      });

    return () => {
      cancelled = true;
    };
  }, [user?.role]);

  return count;
}

export function SiteHeader() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuthStore();
  const inboxCount = useInboxCount();

  const displayName = user?.name ?? 'Admin';

  /**
   * Same predicate as RootPage, PublicNav and MobileNav — one spelling of "is
   * this an admin" across every surface, because two spellings is how one of
   * them ends up showing the console to someone the others hide it from.
   *
   * `ProtectedRoute` gates /admin on being SIGNED IN, not on being an admin, so
   * a sailor who follows a stale link reaches this header. The API refuses them
   * at every route behind it, but the nav should not hand them the map.
   */
  const isAdmin = user?.role === 'admin' || user?.role === 'superadmin';

  const navItems = isAdmin ? [
    { label: 'Dashboard', icon: LayoutDashboard, path: '/admin' },
    { label: 'Inbox', icon: Inbox, path: '/admin/inbox' },
    // #1478: user-submitted races. Next to Inbox because it is the same kind of
    // work — something arrived, somebody has to build it.
    { label: 'Pending races', icon: Flag, path: '/admin/pending-races' },
    { label: 'Video Locker', icon: Video, path: '/admin/video-locker' },
    { label: 'Ingestion', icon: Database, path: '/admin/ingestion' },
    { label: 'Standings', icon: Trophy, path: '/admin/standings-import' },
    { label: 'Users', icon: Users, path: '/admin/users' },
  ] : [];

  return (
    <header className="h-14 bg-navy-900 text-white flex items-center px-3 md:px-6 shrink-0">
      {/* Mobile hamburger */}
      <MobileNav />

      <Link to="/admin" className="mr-auto ml-2 md:ml-0">
        <BrandLogo variant="dark" />
      </Link>

      {/* Desktop inline nav links */}
      <nav className="hidden md:flex items-center gap-1 mr-3">
        {navItems.map((item) => {
          const isActive =
            item.path === '/admin'
              ? location.pathname === '/admin'
              : location.pathname.startsWith(item.path);
          return (
            <Link
              key={item.path}
              to={item.path}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded text-sm transition-colors ${
                isActive
                  ? 'bg-white/15 text-white'
                  : 'text-white/70 hover:bg-white/10 hover:text-white'
              }`}
            >
              <item.icon className="h-3.5 w-3.5" />
              {item.label}
              {item.label === 'Inbox' && inboxCount > 0 && (
                <span className="inline-flex items-center justify-center rounded-full bg-brand-blue !text-navy-900 text-xs font-medium px-1.5 py-0.5 min-w-[18px]">
                  {inboxCount}
                </span>
              )}
            </Link>
          );
        })}
      </nav>

      {/* User dropdown */}
      <DropdownMenu>
        <DropdownMenuTrigger className="flex items-center gap-1 px-3 py-1.5 rounded text-sm hover:bg-white/10 transition-colors min-h-[44px] md:min-h-0">
          <span className="hidden sm:inline">{displayName}</span>
          <span className="sm:hidden flex h-7 w-7 items-center justify-center rounded-full bg-white/20 text-xs font-medium">
            {displayName.charAt(0).toUpperCase()}
          </span>
          <ChevronDown className="h-3.5 w-3.5 hidden sm:block" />
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem
            onClick={() => {
              logout();
              navigate('/login');
            }}
          >
            <LogOut className="mr-2 h-4 w-4" />
            Sign Out
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </header>
  );
}
